import React, { useEffect, useState } from "react";
import { AUTH_STORAGE_KEY } from "../../socket";
import { loadLabels } from "../../api/labelsApi";
import MailListItem from "./MailListItem";
import { fetchMailStats, updateMailStatus, moveMailsBulk } from "../../api/mailApi";
import { useNavigate } from "react-router-dom";

function MailList({
  mails = [],
  searchQuery = "",
  setSearchQuery = () => {},
  selectedMailId,
  onSelectMail,
  folder,
  onToggleStar,
  starredMap = {},
  selectedIds = [],
  onToggleSelect = () => {},
  onToggleSelectAll = () => {},
  onMarkReadSelected = () => {},
  onMarkUnreadSelected = () => {},
  onStarSelected = () => {},
  onUnstarSelected = () => {},
  onMoveSelected = () => {},
  onDeleteSelected = () => {},
  onArchiveSelected = () => {},
  onReportSpamSelected = () => {},
  onApplyLabelSelected = () => {},
  onRemoveLabelSelected = () => {},
  onStatusFilter = () => {},
  activeFolder = "inbox",
  onFolderChange = () => {},
  isMobile = false,
  onOpenComposeModal, // <-- NEW PROP,
  // onMoveSelected={handleMoveSelected}

}) {
  const anySelected = selectedIds.length > 0;
  const allSelected = mails.length > 0 && mails.every(m => selectedIds.includes(m.id));

  const [labels,       setLabels]       = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [mailStats,    setMailStats]    = useState({ total:0, sent:0, open:0, inProgress:0, closed:0 });
const [showMoveMenu, setShowMoveMenu] = useState(false);
const [moveMenuPos, setMoveMenuPos] = useState({ x: 0, y: 0 });
const navigate = useNavigate();
const [menu, setMenu] = useState(null);
  // ── Mail stats ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const headers = parsed?.token ? { Authorization:`Bearer ${parsed.token}` } : null;
        const res = await fetchMailStats(headers);
        setMailStats({
          total:      Number(res.total)      || 0,
          sent:       Number(res.sent)       || 0,
          open:       Number(res.open)       || 0,
          inProgress: Number(res.inProgress) || 0,
          closed:     Number(res.closed)     || 0,
        });
      } catch (err) { console.error("Mail stats error:", err); }
    };
    load();
  }, [mails]);

  // ── Labels ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const headers = parsed?.token ? { Authorization:`Bearer ${parsed.token}` } : null;
        if (!headers) { setLabels([]); return; }
        const res = await loadLabels(headers);
        if (mounted) setLabels(Array.isArray(res?.labels) ? res.labels : []);
      } catch { if (mounted) setLabels([]); }
    };
    load();
    const handler = () => load();
    window.addEventListener("labels-updated", handler);
    return () => { mounted = false; window.removeEventListener("labels-updated", handler); };
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleStatusChange = async (mail, newStatus) => {
    try {
      const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const headers = parsed?.token ? { Authorization:`Bearer ${parsed.token}` } : null;
      await updateMailStatus(mail.id, newStatus, headers);
      const res = await fetchMailStats(headers);
      setMailStats({ total:Number(res.total)||0, sent:Number(res.sent)||0, open:Number(res.open)||0, inProgress:Number(res.inProgress)||0, closed:Number(res.closed)||0 });
    } catch (err) { console.error("Failed to update mail status", err); }
  };

  const handleMoveToFolder = async (folder) => {
    if (!anySelected) return;
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const headers = parsed?.token ? { Authorization:`Bearer ${parsed.token}` } : null;
      
      await moveMailsBulk(selectedIds, folder, headers);
      
      // Call parent handler to refresh the mail list
      onMoveSelected?.(folder);
      
      console.log(`Moved ${selectedIds.length} mail(s) to ${folder}`);
    } catch (err) {
      console.error("Failed to move mails", err);
      alert("Failed to move mails. Please try again.");
    }
  };

  const handleActionChange = (value) => {
    if (!value || !anySelected) return;
    ({ markRead:onMarkReadSelected, markUnread:onMarkUnreadSelected, star:onStarSelected, unstar:onUnstarSelected, delete:onDeleteSelected, archive:onArchiveSelected, reportSpam:onReportSpamSelected }[value] || (() => {}))();
  };

  // ── Filter / summary ───────────────────────────────────────────────────────
  const filteredMails = mails.filter(mail => {
    if (statusFilter === "all") return true;
    const s = (mail.mailStatus || "").trim().toLowerCase();
    if (statusFilter === "open")     return s === "open";
    if (statusFilter === "action")   return s === "action";
    if (statusFilter === "progress") return s === "in progress";
    if (statusFilter === "closed")   return s === "closed";
    if (statusFilter === "na")       return !s || s === "na";
    return true;
  });

  const statusSummary = mails.reduce((acc, mail) => {
    const s = (mail.mailStatus || "").trim().toLowerCase();
    if      (s === "open")        acc.open++;
    else if (s === "action")      acc.action++;
    else if (s === "in progress") acc.progress++;
    else if (s === "closed")      acc.closed++;
    else                          acc.na++;
    return acc;
  }, { open:0, action:0, progress:0, closed:0, na:0 });

  const FOLDERS = [
    { key:"inbox",   label:"Inbox",   icon:"fa-inbox"        },
    { key:"sent",    label:"Sent",    icon:"fa-paper-plane"  },
    { key:"drafts",  label:"Drafts",  icon:"fa-file-lines"   },
    { key:"starred", label:"Starred", icon:"fa-star"         },
    { key:"archive", label:"Archive", icon:"fa-box-archive"  },
    { key:"trash",   label:"Trash",   icon:"fa-trash"        },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="mail-list">
{menu?.type === "sidebar" && (
  <div
    className="message-menu"
    style={{
      position: "fixed",
      top: menu.y,
      left: menu.x,
      zIndex: 9999,
    }}
  >
    <div
      className="menu-item"
      onClick={() => {
        setMenu(null);
        navigate("/kmail/settings");
      }}
    >
      ✍️ Add Signature
    </div>

    <div
      className="menu-item"
      onClick={() => {
        setMenu(null);
        navigate("/kmail/settings");
      }}
    >
      ⚙️ Settings
    </div>
  </div>
)}
{!anySelected ? (
  <div className="sidebar-top">
     <div className="mail-brand-content">
          <img src="/TimeEdgeIcon.png" alt="TimeEdge Icon" className="mail-brand-icon mobilecsslogo" />
         
        </div>
       <h3 className="app-title1">
  Kriva <span className="chat-blue">Bharat Mail</span>
</h3>
    <div className="sidebar-actions">
      <button className="icon-btn icon-btn" onClick={onOpenComposeModal} title="Compose">
        ➕
      </button>
      <button
  className="icon-btn"
  onClick={(e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    if (menu?.type === "sidebar") setMenu(null);
    else setMenu({ type: "sidebar", x: rect.right - 140, y: rect.bottom + 5 });
  }}
>
  ⋮
</button>
    </div>
  </div>
) : (
  /* ── Selection action bar ── */
  <div className="sidebar-top" style={{ gap: 6 }}>
    {/* Clear selection */}
    <button
      className="icon-btn"
      title="Clear selection"
      onClick={() => onToggleSelectAll(false, mails)}
      style={{ fontSize: 18, minWidth: 32 }}
    >
      ✕
    </button>

    <span style={{ fontWeight: 500, fontSize: 14, minWidth: 28 }}>
      {selectedIds.length}
    </span>

    {/* Select-all checkbox */}
    <input
      type="checkbox"
      checked={allSelected}
      onChange={e => onToggleSelectAll(e.target.checked, mails)}
      title="Select all"
      style={{ marginRight: 4, cursor: "pointer" }}
    />

    <div className="sidebar-actions" style={{ gap: 30 }}>
      <button
  className="icon-btn icon-btn11"
  title="Move To"
  onClick={(e) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();

    setMoveMenuPos({
      x: rect.left,
      y: rect.bottom + 5,
    });

    setShowMoveMenu(!showMoveMenu);
  }}
  style={{ fontSize: 14 }}
>
  📂
  <small className="marlef">Move</small>
</button>
      <button
        className="icon-btn icon-btn11"
        title={folder === "trash" ? "Delete permanently" : "Move to trash"}
        onClick={onDeleteSelected}
        style={{ fontSize: 14, color: "#ef4444" }}
      >
        🗑️
        <small className="marlef">{folder === "trash" ? "Delete" : "Trash"}</small>
      </button>
    </div>
  </div>
)}
{showMoveMenu && (
  <>
    <div
      onClick={() => setShowMoveMenu(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
      }}
    />

    <div
      className="kmail-move-menu"
      style={{
        position: "fixed",
        top: moveMenuPos.y,
        left: moveMenuPos.x,
        zIndex: 9999,
      }}
    >
      <div className="kmail-move-header">
        Move to:
      </div>

      {/* <div className="kmail-search-box">
        <input
          type="text"
          placeholder="Search folder..."
        />
      </div> */}

      <div
        className="kmail-move-item"
        onClick={() => {
          handleMoveToFolder("inbox");
          setShowMoveMenu(false);
        }}
      >
        📥 Inbox
      </div>

      <div
        className="kmail-move-item"
        onClick={() => {
          handleMoveToFolder("sent");
          setShowMoveMenu(false);
        }}
      >
        📤 Sent
      </div>

      <div
        className="kmail-move-item"
        onClick={() => {
          handleMoveToFolder("draft");
          setShowMoveMenu(false);
        }}
      >
        📝 Drafts
      </div>

      <div
        className="kmail-move-item"
        onClick={() => {
          handleMoveToFolder("starred");
          setShowMoveMenu(false);
        }}
      >
        ⭐ Starred
      </div>

      <div
        className="kmail-move-item"
        onClick={() => {
          handleMoveToFolder("archive");
          setShowMoveMenu(false);
        }}
      >
        📦 Archive
      </div>

      {/* <div
        className="kmail-move-item"
        onClick={() => {
          handleMoveToFolder("trash");
          setShowMoveMenu(false);
        }}
      >
        🗑 Trash
      </div> */}

      <div className="kmail-divider" />

      {labels.map((label) => (
        <div
          key={label.id}
          className="kmail-move-item"
          onClick={() => {
            onApplyLabelSelected?.(label.id);
            setShowMoveMenu(false);
          }}
        >
          🏷 {label.name}
        </div>
      ))}
    </div>
  </>
)}
      {/* ── SEARCH ──────────────────────────────────────────────────────────── */}
      <div className="mail-search-box">
        <input
          type="text"
          placeholder="Search mail"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value.toLowerCase())}
        />
      </div>

      {/* ── STATUS SUMMARY ──────────────────────────────────────────────────── */}
      <div className="status-summary-box">
        {[
          { key:"open",     label:"OPEN",     val:statusSummary.open     },
          { key:"progress", label:"PROGRESS", val:statusSummary.progress },
          { key:"action", label:"ACTION", val:statusSummary.action },
          { key:"closed",   label:"CLOSED",   val:statusSummary.closed   },
          // { key:"na",       label:"NA",       val:statusSummary.na       },
        ].map(({ key, label, val }) => (
          <div
            key={key}
            className={`status-card ${key === "na" ? "all" : key} ${statusFilter === key ? "active" : ""}`}
            onClick={() => { setStatusFilter(key); onStatusFilter(key); }}
          >
            <h3>{val}</h3>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* ── FOLDER TABS (below status bar) ──────────────────────────────────── */}
      <div className="mail-folder-tabs">
        {FOLDERS.map(({ key, label, icon }) => (
          <button
            key={key}
            className={`mail-folder-tab ${activeFolder === key ? "active" : ""}`}
            onClick={() => onFolderChange(key)}
          >
            <i className={`fa-solid ${icon}`} />
            <span className="tab-label">{label}</span>
          </button>
        ))}
      </div>

      {/* ── BULK ACTION BAR ─────────────────────────────────────────────────── */}
      <div className="mail-toolbar-actions" style={{ display: "none"}}>
        <input
          type="checkbox" style={{ width: 20 }}
          checked={allSelected}
          onChange={e => onToggleSelectAll(e.target.checked, mails)}
          title="Select all"
        />

        <button title="Delete"  className="mail-action-small" disabled={!anySelected} onClick={onDeleteSelected}>
          <i className="fa-solid fa-trash" />
        </button>

        <button title="Archive" className="mail-action-small" disabled={!anySelected} onClick={onArchiveSelected}>
          <i className="fa-solid fa-box-archive" />
        </button>

        <select
          className="mail-action-small"
          disabled={!anySelected || labels.length === 0}
          onChange={e => { const v = e.target.value; e.target.value = ""; if (v) onApplyLabelSelected?.(v); }}
          value=""
        >
          <option value="">🏷️</option>
          {labels.map(lbl => <option key={lbl.id} value={lbl.id}>{lbl.name}</option>)}
        </select>

        <select
          value=""
          className="mail-more-select"
          disabled={!anySelected}
          onChange={e => { handleActionChange(e.target.value); e.target.value = ""; }}
        >
          <option value="">More</option>
          <option value="markRead">Mark Read</option>
          <option value="markUnread">Mark Unread</option>
          <option value="star">Star</option>
          <option value="unstar">Unstar</option>
        </select>
      </div>

      {/* ── MAIL LIST ───────────────────────────────────────────────────────── */}
      {Array.isArray(filteredMails) && filteredMails.length > 0 ? (
        filteredMails.map(mail => (
          <MailListItem
            key={mail.id}
            mail={mail}
            folder={folder}
            selected={selectedMailId === mail.id}
            starredMap={starredMap}
            onSelectMail={onSelectMail}
            onToggleStar={onToggleStar}
            isChecked={selectedIds.includes(mail.id)}
            onToggleSelect={onToggleSelect}
            isMobile={isMobile}
            onStatusChange={handleStatusChange}
            
          />
        ))
      ) : (
        <div className="mail-empty">No messages found</div>
      )}

    </div>
  );
}

export default MailList;
